import React, {
    useEffect,
    useRef,
    useState,
    forwardRef,
    useImperativeHandle,
} from 'react';
import Modal from '../../shared/modal';
import Button from '../../shared/button';
import { useAppDispatch } from '../../app/hooks';
import { tryToSignoffReport } from '../../store/admin/reports/reportsSlice';
import Error from '../../shared/error';

type DocEditorProps = {
    open: boolean;
    onClose: () => void;
    url: string;
    data?: any;
    fileName?: string;
    className?: string;
};

type DocEditorRef = { prepare: () => Promise<FormData> };

const DocEditor = forwardRef<DocEditorRef, DocEditorProps>(function DocEditor(
    { open, onClose, data, url, fileName, className }: DocEditorProps,
    ref
) {
    const dispatch = useAppDispatch();
    const fileInputRef = React.useRef<HTMLInputElement | null>(null);
    const [loading, setLoading] = useState(true);
    const [viewerErrored, setViewerErrored] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [disableButtons, setDisableButtons] = useState<any>();
    const [signOffError, setSignOffError] = useState<string>()
    const iframeRef = useRef<HTMLIFrameElement | null>(null);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        setViewerErrored(false);
        setReloadKey((k) => k + 1);
    }, [open, url]);

    useImperativeHandle(ref, () => ({
        async prepare() {
            const fd = new FormData();
            if (fileName) fd.append('fileName', fileName);
            if (url) fd.append('url', url);
            return fd;
        },
    }));

    const onFileChange = async (event: any) => {
        try {
            setSignOffError(undefined)
            const formData = new FormData()
            formData.append('file', event?.target?.files)
            formData.append('tripId', `${data?.tripId}`)
            await dispatch(tryToSignoffReport(formData)).unwrap();
            setDisableButtons(true)
        } catch (err) {
            setSignOffError(`${err}`)
            setDisableButtons(false)
        }
    };

    const onSignOffExistingDoc = async () => {
        try {
            setSignOffError(undefined)
            const formData = new FormData()
            formData.append('tripId', `${data?.tripId}`)
            await dispatch(tryToSignoffReport(formData)).unwrap();
            setDisableButtons(true)
        } catch (err) {
            setSignOffError(`${err}`)
            setDisableButtons(false)
        }
    }

    const handleLoad = () => setLoading(false);
    const handleError = () => {
        setViewerErrored(true);
        setLoading(false);
    };
    const onUploadClick = () => {
        fileInputRef.current?.click();
    };

    const officeSrc = url ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}&v=${Date.now()}` : '';
    return (
        <Modal
            open={open}
            onClose={onClose}
            title={'Trip Report'}
            contentContainerStyle='!rounded-xl min-w-[95%] md:min-w-[90%] lg:min-w-[80%]'
            headerStyle='border-b-1 py-3 mb-4'>
            <div className={`p-2 max-w-[95vw] md:min-w-[70%] lg:min-w-[70%] min-h-[500px] ${className || ''}`}>
                {signOffError && <Error text={signOffError} />}
                <div className='my-4 flex flex-row justify-between items-start'>
                    <div className='flex flex-row items-start'>
                        <div className='mr-3'>
                            <Button
                                label={'Upload & Sign Off'}
                                onClickButton={() => disableButtons && onUploadClick()}
                                className={`btn-primary-rounded-less ${disableButtons ? 'opacity-60 cursor-not-allowed' : ''}`}
                            />
                            <input
                                type='file'
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={onFileChange}
                                accept='*'
                            />
                        </div>
                        <Button
                            label={'Sign Off Current Report'}
                            onClickButton={() => disableButtons && onSignOffExistingDoc()}
                            className={`btn-primary-rounded-less ${disableButtons ? 'opacity-60 cursor-not-allowed' : ''} ml-3`}
                        />
                    </div>
                    <Button
                        label={'Download Report'}
                        onClickButton={() => window.open(url, '_blank')}
                        className='btn-primary-rounded-less ml-3'
                    />
                </div>
                {!url ? (
                    <div className='text-sm text-red-600'>
                        Missing file URL. Provide a public .doc/.docx link.
                    </div>
                ) : (
                    <div className='w-full h-[70vh] border rounded-lg overflow-hidden relative bg-white'>
                        <div className='flex items-center justify-between px-3 py-2 border-b bg-white'>
                            <div className='text-sm font-medium truncate'>
                                {fileName || ''}
                            </div>

                        </div>

                        <iframe
                            key={reloadKey}
                            ref={iframeRef}
                            title='Trip Report'
                            src={officeSrc}
                            className='w-full h-[calc(70vh-41px)]'
                            frameBorder='0'
                            referrerPolicy='no-referrer'
                            onLoad={handleLoad}
                            onError={handleError}
                        />

                        {loading && (
                            <div className='absolute inset-0 flex items-center justify-center bg-white/70'>
                                <div className='text-sm'>Loading preview…</div>
                            </div>
                        )}
                        {viewerErrored && (
                            <div className='absolute bottom-2 left-2 right-2 text-xs text-red-600'>
                                Couldn’t load the preview. Ensure the URL is public and the
                                presigned link hasn’t expired.
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
});

export default DocEditor;
